import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Badge } from 'react-bootstrap';
import { fetchStreams, fetchStreamStats } from '../../../store/actions/StreamActions';
import useWebSocket from '../../../hooks/useWebSocket';

const StreamDashboard = ({
  streams = [],
  stats = {},
  loading = false,
  fetchStreams,
  fetchStreamStats,
}) => {
  const navigate = useNavigate();
  const { isConnected, streamStats = {}, streamUpdates = {} } = useWebSocket();

  const [realtimeStats, setRealtimeStats] = useState({
    total_streams: 0,
    active_streams: 0,
    total_viewers: 0,
    monthly_bandwidth: 0,
    storage_used: 0,
    storage_total: 0,
    nginx_stats: {
      total_bandwidth_in: 0,
      total_bandwidth_out: 0,
      total_clients: 0,
    },
  });

  const calculatedActiveStreams = useMemo(() => {
    if (streamStats && Number(streamStats.total_streams) > 0) {
      return Number(streamStats.total_streams);
    }

    const updateKeys = Object.keys(streamUpdates || {});
    if (updateKeys.length > 0) {
      let count = 0;
      Object.values(streamUpdates).forEach((update) => {
        if (Number(update?.clients || 0) > 0 || Number(update?.bandwidth_in || 0) > 0) {
          count += 1;
        }
      });
      return count;
    }

    return Number(stats?.active_streams || 0);
  }, [streamStats, streamUpdates, stats]);

  useEffect(() => {
    setRealtimeStats((prev) => ({
      ...prev,
      total_streams: Number(stats?.total_streams || prev.total_streams || streams.length),
      active_streams: calculatedActiveStreams,
      total_viewers: Number(
        stats?.total_viewers ??
          streamStats?.total_hls_viewers ??
          streamStats?.total_clients ??
          prev.total_viewers ??
          0
      ),
      monthly_bandwidth: Number(stats?.monthly_bandwidth || prev.monthly_bandwidth || 0),
      storage_used: Number(stats?.storage_used || prev.storage_used || 0),
      storage_total: Number(stats?.storage_total || prev.storage_total || 0),
      nginx_stats: {
        total_bandwidth_in: Number(
          stats?.nginx_stats?.total_bandwidth_in ??
            streamStats?.nginx_stats?.total_bandwidth_in ??
            streamStats?.total_bandwidth_in ??
            prev.nginx_stats.total_bandwidth_in ??
            0
        ),
        total_bandwidth_out: Number(
          stats?.nginx_stats?.total_bandwidth_out ??
            streamStats?.nginx_stats?.total_bandwidth_out ??
            streamStats?.total_bandwidth_out ??
            prev.nginx_stats.total_bandwidth_out ??
            0
        ),
        total_clients: Number(
          stats?.nginx_stats?.total_clients ??
            streamStats?.nginx_stats?.total_clients ??
            streamStats?.total_clients ??
            prev.nginx_stats.total_clients ??
            0
        ),
      },
    }));
  }, [stats, streamStats, calculatedActiveStreams, streams.length]);

  useEffect(() => {
    fetchStreams();
    fetchStreamStats();

    const interval = setInterval(() => {
      fetchStreams();
      fetchStreamStats();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchStreams, fetchStreamStats]);

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'broadcasting':
      case 'live':
      case 'active':
        return 'success';
      case 'inactive':
        return 'secondary';
      case 'disconnected':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const streamsList = Array.isArray(streams) ? streams : [];
  const activeStreams = streamsList.filter(
    (s) =>
      s?.is_live === true ||
      ['broadcasting', 'live', 'active'].includes((s?.status || '').toLowerCase())
  );
  const recentStreams = streamsList.slice(0, 5);

  const getStreamWithRealtimeData = (stream) => {
    const wsUpdate = streamUpdates?.[stream?.stream_key];
    if (!wsUpdate) {
      return {
        ...stream,
        status: stream?.is_live ? 'broadcasting' : stream?.status,
      };
    }

    const wsIsLive = wsUpdate?.isLive !== undefined ? !!wsUpdate.isLive : !!stream?.is_live;

    return {
      ...stream,
      viewer_count:
        wsUpdate?.clients !== undefined ? Number(wsUpdate.clients) : Number(stream?.viewer_count || 0),
      is_live: wsIsLive,
      status: wsIsLive ? 'broadcasting' : (stream?.status || 'inactive'),
    };
  };

  return (
    <>
      <div className="form-head d-flex mb-3 mb-md-4 align-items-start">
        <div className="me-auto d-none d-lg-block">
          <h3 className="text-black font-w600">WATCHO Dashboard</h3>
          <p className="mb-0">Best Video Streaming Software - Manage your streams</p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            onClick={() => {
              fetchStreams();
              fetchStreamStats();
            }}
            disabled={loading}
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`} aria-hidden="true"></i>
          </Button>
          <Button variant="primary" onClick={() => navigate('/create-stream')}>
            <i className="fa fa-plus me-2" aria-hidden="true"></i>
            New Stream
          </Button>
        </div>
      </div>

      <Row className="mb-4">
        <Col xl={3} lg={6} md={6} className="mb-3">
          <Card>
            <Card.Body className="d-flex align-items-center">
              <div className="me-3">
                <div className="rounded-circle bg-primary text-white d-flex justify-content-center align-items-center" style={{ width: 44, height: 44 }}>
                  <i className="fas fa-video"></i>
                </div>
              </div>
              <div>
                <h4 className="mb-0">{Number(realtimeStats.total_streams || streamsList.length)}</h4>
                <small className="text-muted">Total Streams</small>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={3} lg={6} md={6} className="mb-3">
          <Card>
            <Card.Body className="d-flex align-items-center">
              <div className="me-3">
                <div className="rounded-circle bg-success text-white d-flex justify-content-center align-items-center" style={{ width: 44, height: 44 }}>
                  <i className="fas fa-broadcast-tower"></i>
                </div>
              </div>
              <div>
                <h4 className="mb-0 text-success">{calculatedActiveStreams}</h4>
                <small className="text-muted">Live Now</small>
                {isConnected && calculatedActiveStreams > 0 && (
                  <small className="text-success d-block">Broadcasting</small>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={3} lg={6} md={6} className="mb-3">
          <Card>
            <Card.Body className="d-flex align-items-center">
              <div className="me-3">
                <div className="rounded-circle bg-info text-white d-flex justify-content-center align-items-center" style={{ width: 44, height: 44 }}>
                  <i className="fas fa-users"></i>
                </div>
              </div>
              <div>
                <h4 className="mb-0 text-info">{Number(realtimeStats.total_viewers || 0)}</h4>
                <small className="text-muted">Live Viewers</small>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={3} lg={6} md={6} className="mb-3">
          <Card>
            <Card.Body className="d-flex align-items-center">
              <div className="me-3">
                <div className="rounded-circle bg-warning text-white d-flex justify-content-center align-items-center" style={{ width: 44, height: 44 }}>
                  <i className="fas fa-wifi"></i>
                </div>
              </div>
              <div>
                <h4 className="mb-0">
                  {Math.round(
                    Number(
                      realtimeStats.nginx_stats.total_bandwidth_in ||
                      realtimeStats.nginx_stats.ingest_bitrate ||
                      0
                    ) / (1024 * 1024)
                  )} MB/s
                </h4>
                <small className="text-muted">Current Ingest</small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={8} className="mb-4">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                Active Streams ({activeStreams.length})
                {isConnected && <Badge bg="success" className="ms-2">LIVE</Badge>}
              </h5>
              <Button variant="link" onClick={() => navigate('/streams')}>View All</Button>
            </Card.Header>
            <Card.Body>
              {activeStreams.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="fas fa-video-slash fa-2x mb-3"></i>
                  <p className="mb-3">No active streams right now.</p>
                  <Button variant="primary" size="sm" onClick={() => navigate('/create-stream')}>
                    Create Your First Stream
                  </Button>
                </div>
              ) : (
                <Row>
                  {activeStreams.map((stream) => {
                    const merged = getStreamWithRealtimeData(stream);
                    return (
                      <Col md={6} key={merged.id} className="mb-3">
                        <Card className="h-100">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <h6 className="mb-0">{merged.name || merged.title || 'Untitled Stream'}</h6>
                              <Badge bg={getStatusColor(merged.status)}>{merged.status || 'inactive'}</Badge>
                            </div>
                            <small className="text-muted d-block mb-2">
                              Viewers: {Number(merged.viewer_count || 0)}
                            </small>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="w-100"
                              onClick={() => navigate(`/streams/${merged.id}`)}
                            >
                              Open Stream
                            </Button>
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Recent Streams</h5>
            </Card.Header>
            <Card.Body>
              {recentStreams.length === 0 ? (
                <small className="text-muted">No streams available.</small>
              ) : (
                recentStreams.map((stream) => (
                  <div key={stream.id} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <div>
                      <div className="fw-semibold">{stream.name || stream.title || 'Untitled Stream'}</div>
                      <small className="text-muted">{stream.stream_key || '-'}</small>
                    </div>
                    <Badge bg={getStatusColor(stream.status)}>{stream.status || 'inactive'}</Badge>
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

const mapStateToProps = (state) => ({
  streams: state?.streams?.streams || [],
  stats: state?.streams?.stats || {},
  loading: state?.streams?.loading || false,
});

const mapDispatchToProps = {
  fetchStreams,
  fetchStreamStats,
};

export default connect(mapStateToProps, mapDispatchToProps)(StreamDashboard);
